import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import styles from "../../_css/ChatPopup.module.css";
import { X, ArrowUp, PhoneOff, Pause, Play } from "lucide-react";
import SessionActions from "./SessionActions";
import {
  chatMessage,
  endSession,
  startChat,
  updateSessionDuration,
} from "../../requests";
import { deleteAuthToken, notifyError } from "../../utils/helper";
import ResponseLoader from "../common/ResponseLoader";
import DeleteUserModal from "../Modals/DeleteUserModal";
import { useSelector } from "react-redux";
import { CHAT_POPUP, DOWNLOAD_SUMMARY_MODAL } from "../../data/modelsData";
import {
  CustomButton,
  CustomButtonOutline,
  CustomButtonOutlineLight,
} from "../UI/CustomButtons";
import { formatHTMLResponse, isHTML } from "../helper/formatters";
import ERROR_MSGS from "../../data/errorMsgs";

const AI_AVATAR = `${process.env.PUBLIC_URL}/img/user-icon.png`;
const DR_AVATAR = `${process.env.PUBLIC_URL}/img/dr-salah-icon.png`;

const DEFAULT_TIMER_MINUTES = 45;
const DEFAULT_TIMER = "45:00";

const summaryMessages = [
  {
    sender: "ai",
    text: "A calming, guided session designed to help users release physical pain and emotional tension through focused breathing, body awareness, and mindful relaxation techniques. This exercise promotes inner balance and supports overall well-being.A calming, guided session designed to help users release physical pain and emotional tension through focused breathing, body awareness, and mindful relaxation techniques. This exercise promotes inner balance and supports overall well-being.A calming, guided session designed to help users release physical pain and emotional tension through focused breathing, body awareness, and mindful relaxation techniques. This exercise promotes inner balance and supports overall well-being.A calming, guided session designed to help users release physical pain and emotional tension through focused breathing, body awareness, and mindful relaxation techniques. This exercise promotes inner balance and supports overall well-being.",
    summary: true,
  },
];

const ChatPopup = ({
  show,
  onClose,
  setShowDownloadSummary,
  header,
  refreshReviews,
  onDownload,
  pdfStatus,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [waiting, setWaiting] = useState(false);
  const [hovered, setHovered] = useState("");
  const [showSaveSession, setShowSaveSession] = useState(false);
  const [summary, setSummary] = useState(false);
  const [timer, setTimer] = useState("00:00");
  const [defaultTimer, setDefaultTimer] = useState(null);
  const [userSessionId, setUserSessdionId] = useState(null);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [spentSeconds, setSpentSeconds] = useState(0);
  const [sessionHistory, setSessionHistory] = useState(false);
  const [sessionStatus, setSessionStatus] = useState(null);
  const [startCountdown, setStartCountdown] = useState(false);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [isSessionPaused, setIsSessionPaused] = useState(false);

  const lang = useSelector((state) => state.language.lang);
  const {
    summaryText,
    inputPlaceholder,
    endSessionText,
    sureText,
    endSessionBtnText,
    cancelText,
    continueSessionText,
    sessionCantBeContinuedText,
  } = CHAT_POPUP[lang];
  const { btnText } = DOWNLOAD_SUMMARY_MODAL[lang];

  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  const sessionData = sessionStorage.getItem("sessionData");
  const sessionDataParsed = JSON.parse(sessionData);
  const sessionId = sessionDataParsed?.id;

  const userInfo = JSON.parse(sessionStorage.getItem("sal_userInfo"));
  const userName = userInfo?.fullName || "User";

  // Countdown timer effect
  useEffect(() => {
    if (!show || !defaultTimer || !startCountdown || isSessionPaused) return;

    console.log("starting default timer countdown: ", defaultTimer);

    // Convert MM:SS format to total seconds
    const [minutes, seconds] = defaultTimer?.split(":").map(Number);
    let totalSeconds = minutes * 60 + seconds;
    const totalTime = DEFAULT_TIMER_MINUTES * 60;

    const interval = setInterval(() => {
      if (totalSeconds <= 0) {
        clearInterval(interval);
        onClose?.(); // Call onClose when countdown reaches 00:00
        handleEndSession();
        return;
      }

      totalSeconds--;
      // Convert back to MM:SS format
      const mins = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;
      const newTimer = `${mins.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;

      setSpentSeconds(totalTime - totalSeconds);
      setTimer(newTimer);
    }, 1000);

    // Cleanup interval on unmount or when show changes
    return () => clearInterval(interval);
  }, [show, onClose, defaultTimer, startCountdown, isSessionPaused]);

  useEffect(() => {
    if (userSessionId && spentSeconds > 0) {
      updateSessionDuration({
        userSessionId,
        duration: spentSeconds,
      });
    }
  }, [spentSeconds, userSessionId]);

  useEffect(() => {
    // resume session block
    let sessionResume = false;
    const resumeSessionData = sessionStorage.getItem("resumeSessionData");
    const chatHistory = sessionStorage.getItem("chatHistory");

    if (resumeSessionData && chatHistory) {
      sessionResume = true;
      const resumeSessionDataParsed = JSON.parse(resumeSessionData);
      setSessionHistory(true);
      setSessionStatus(resumeSessionDataParsed?.status);

      // set user session id
      const userSessionId = resumeSessionDataParsed?.userSessionId;
      setUserSessdionId(userSessionId);

      // set timer
      const duration = resumeSessionDataParsed?.duration;
      const remainingDuration = DEFAULT_TIMER_MINUTES * 60 - duration;
      const mins = Math.floor(remainingDuration / 60);
      const secs = remainingDuration % 60;
      const newTimer = `${mins.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;

      console.log("setting newTimer: ", newTimer);
      setDefaultTimer(newTimer);

      // set chat history
      const chatHistoryParsed = JSON.parse(chatHistory);
      setMessages(chatHistoryParsed);
    } else if (show) {
      setStartCountdown(true);
      setSessionHistory(false);
      console.log("setting default timer: ", DEFAULT_TIMER);
      setDefaultTimer(DEFAULT_TIMER);
    }

    if (show) {
      if (!sessionResume) {
        handleStartChat();
      }
    } else {
      resetStates();
    }
    return () => {
      resetStates();
    };
  }, [show]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, [messages]);

  // Keyboard detection for mobile
  useEffect(() => {
    if (!show) return;

    const handleResize = () => {
      const initialViewportHeight = window.innerHeight;
      const currentViewportHeight =
        window.visualViewport?.height || window.innerHeight;

      // If viewport height decreased significantly, keyboard is likely open
      const heightDifference = initialViewportHeight - currentViewportHeight;
      setIsKeyboardOpen(heightDifference > 150);
    };

    const handleVisualViewportChange = () => {
      if (window.visualViewport) {
        const heightDifference =
          window.innerHeight - window.visualViewport.height;
        setIsKeyboardOpen(heightDifference > 150);
      }
    };

    // Listen for viewport changes
    if (window.visualViewport) {
      window.visualViewport.addEventListener(
        "resize",
        handleVisualViewportChange
      );
    } else {
      window.addEventListener("resize", handleResize);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          "resize",
          handleVisualViewportChange
        );
      } else {
        window.removeEventListener("resize", handleResize);
      }
    };
  }, [show]);

  const resetStates = () => {
    setMessages([]);
    setInput("");
    setWaiting(false);
    setHovered("");
    setShowSaveSession(false);
    setDefaultTimer(null);
    setSessionHistory(false);
    setSessionStatus(null);
    setStartCountdown(false);
    setIsKeyboardOpen(false);
    setIsSessionPaused(false);
  };

  const handleEndSession = async () => {
    try {
      const response = await endSession({
        userSessionId,
      });
      if (response.status === 200) {
        refreshReviews();
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.error("Error Initiating Chat:", error);
    }
    setShowDownloadSummary(true);
    setShowDeleteUserModal(false);
  };

  const handleStartChat = async () => {
    setWaiting(true);
    try {
      const response = await startChat({
        sessionId,
        lang,
      });
      // Trigger a refresh of the current page to get updated data

      let aiText = response?.data?.message;
      if (!aiText) {
        aiText = ERROR_MSGS[lang].chatStartError;
      }
      let userSessionId = response?.data?.userSessionId;
      if (!userSessionId) {
        userSessionId = undefined;
        sessionStorage.removeItem("userSessionId");
        setUserSessdionId(null);
      } else {
        sessionStorage.setItem("userSessionId", userSessionId);
      }
      setInput("");
      setUserSessdionId(userSessionId);
      setMessages([{ sender: "ai", text: aiText }]);
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      } else if (error?.response?.status === 400) {
        notifyError(error?.response?.data?.message[lang]);
        onClose?.();
      }
      sessionStorage.removeItem("userSessionId");
      setUserSessdionId(null);
      let aiText =
        ERROR_MSGS[lang].chatStartError;
      setInput("");
      setMessages([{ sender: "ai", text: aiText }]);
      console.error("Error Initiating Chat:", error);
    }
    setWaiting(false);
  };

  const handleChat = async (userMessage) => {
    if (!messages.length) {
      return;
    } else if (!userSessionId) {
      return ERROR_MSGS[lang].chatStartError;
    }
    try {
      const response = await chatMessage({
        userSessionId,
        user_msg: userMessage,
      });
      const aiText = response.data.message;

      return aiText;
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.error("Error Sending Message:", error);
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;
    // Auto-resume session if paused when user sends a message
    if (isSessionPaused) {
      setIsSessionPaused(false);
    }

    // to regenerate summary
    if (summary) {
      setSummary(false);
    }
    setMessages((msgs) => [...msgs, { sender: userName, text: input }]);
    setInput("");
    setWaiting(true);
    const aiText = await handleChat(input);
    setMessages((msgs) => [...msgs, { sender: "ai", text: aiText }]);
    setWaiting(false);
  };

  const handleGenerateSummary = () => {
    setInput("");
    setWaiting(true);
    setTimeout(() => {
      setMessages((msgs) => [...msgs, ...summaryMessages]);
      setWaiting(false);
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }, 1500 + Math.random() * 1000);
    setSummary(true);
    setShowSaveSession(false);
  };

  const handleResumeSession = () => {
    setSessionHistory(false);
    setStartCountdown(true);
  };

  const handlePauseResumeSession = () => {
    setIsSessionPaused(!isSessionPaused);
  };

  return (
    <>
      {show && (
        <div className={styles.overlay}>
          <div
            className={`${styles.popup} ${
              isKeyboardOpen ? styles.keyboardOpen : ""
            }`}
          >
            <div className={styles.header}>
              <h4 className={styles.title}>{header}</h4>
              <button className={styles.closeBtn} onClick={onClose}>
                <X size={22} />
              </button>
            </div>
            <div className={styles.chatBody}>
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={
                    msg.sender === userName ? styles.drMsg : styles.aiMsg
                  }
                >
                  <div className={styles.msgText} style={{ overflowX: "auto" }}>
                    <div className={styles.avatar}>
                      <i>
                        <img
                          src={msg.sender === userName ? AI_AVATAR : DR_AVATAR}
                          alt="avatar"
                        />
                      </i>

                      <span>{msg.sender === userName ? userName : ""}</span>
                    </div>
                    {msg?.summary &&
                      (isHTML(summaryText) ? (
                        <strong
                          dangerouslySetInnerHTML={{
                            __html: formatHTMLResponse(summaryText),
                          }}
                        >
                          {summaryText}
                        </strong>
                      ) : (
                        <strong>{summaryText}</strong>
                      ))}
                    {isHTML(msg.text) ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: formatHTMLResponse(msg.text),
                        }}
                      />
                    ) : (
                      <p className="m-0 mt-2">{formatHTMLResponse(msg.text)}</p>
                    )}
                  </div>
                </div>
              ))}
              {waiting && (
                <div className={styles.aiMsg}>
                  <div className={styles.msgText}>
                    <div className={styles.avatar}>
                      <i>
                        <img src={DR_AVATAR} alt="avatar" />
                      </i>
                    </div>

                    <ResponseLoader />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <hr className="m-0" />
            {!sessionHistory ? (
              <div className={styles.inputRow}>
                <input
                  className={styles.input}
                  value={input}
                  ref={inputRef}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !waiting && !isSessionPaused)
                      handleSend();
                  }}
                  placeholder={
                    isSessionPaused
                      ? "Session is paused - click resume to continue"
                      : inputPlaceholder
                  }
                  disabled={waiting || isSessionPaused}
                />
                <div className={styles.bottomBar}>
                  <span className={styles.bottomIconTime}>{timer}</span>

                  <div
                    onClick={() => {
                      if (waiting || !input.trim() || isSessionPaused) return;
                      handleSend();
                    }}
                    className={
                      styles.bottomIconWrap +
                      (waiting || !input.trim() || isSessionPaused
                        ? " " + styles.disabled
                        : "")
                    }
                  >
                    <ArrowUp className={styles.bottomIcon} />
                  </div>

                  <div
                    className={styles.bottomIconWrap}
                    onMouseEnter={() => setHovered("pauseResume")}
                    onMouseLeave={() => setHovered("")}
                    onClick={handlePauseResumeSession}
                  >
                    {isSessionPaused ? (
                      <Play className={styles.bottomIcon} />
                    ) : (
                      <Pause className={styles.bottomIcon} />
                    )}
                    <span
                      className={
                        styles.bottomTextAnimated + " " + hovered ===
                        "pauseResume"
                          ? styles.bottomTextShow
                          : styles.bottomTextHide
                      }
                    >
                      {isSessionPaused ? "Resume Session" : "Pause Session"}
                    </span>
                  </div>

                  <div
                    className={styles.bottomIconWrapDanger}
                    onMouseEnter={() => setHovered("endSession")}
                    onMouseLeave={() => setHovered("")}
                    onClick={() => setShowDeleteUserModal(true)}
                  >
                    <PhoneOff className={styles.bottomIcon} />
                    <span
                      className={
                        styles.bottomTextAnimated + " " + hovered ===
                        "endSession"
                          ? styles.bottomTextShow
                          : styles.bottomTextHide
                      }
                    >
                      {endSessionText}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={`${styles.inputRowSummary} d-flex justify-content-between`}
              >
                <span>
                  {sessionStatus === 0
                    ? continueSessionText
                    : sessionCantBeContinuedText}
                </span>
                <div className={`d-flex gap-2 ${styles.buttonRowSummary}`}>
                  <CustomButtonOutline
                    text={lang === "ar" ? "استمرار الجلسة" : "Resume Session"}
                    onClick={handleResumeSession}
                    width="100%"
                    disabled={sessionStatus !== 0}
                  />
                  <CustomButton
                    icon="download"
                    gap="8px"
                    text={pdfStatus || btnText[1]}
                    onClick={onDownload}
                    width="100%"
                    disabled={pdfStatus}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {showSaveSession &&
        createPortal(
          <SessionActions
            show={showSaveSession}
            onHide={() => setShowSaveSession(false)}
            generateSummary={handleGenerateSummary}
            saveSession={() => {}}
            summary={summary}
          />,
          document.body
        )}
      {showDeleteUserModal &&
        createPortal(
          <DeleteUserModal
            show={showDeleteUserModal}
            onHide={() => setShowDeleteUserModal(false)}
            onEndSession={handleEndSession}
            title={sureText}
            btnText={endSessionBtnText}
            cancelText={cancelText}
          />,
          document.body
        )}
    </>
  );
};

export default ChatPopup;
